import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/* ─── Haversine distance (miles) ─── */
function haversine(lat1, lon1, lat2, lon2) {
    const R = 3958.8; // Earth radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const LocationContext = createContext(null);

export function useLocation() {
    const ctx = useContext(LocationContext);
    if (!ctx) throw new Error('useLocation must be used within LocationProvider');
    return ctx;
}

export default function LocationProvider({ children }) {
    const [position, setPosition] = useState(null);      // { lat, lng }
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [permissionState, setPermissionState] = useState('prompt'); // prompt | granted | denied
    const watchRef = useRef(null);

    /* Check permission status on mount */
    useEffect(() => {
        const localWatchRef = watchRef.current;
        if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' }).then(result => {
                setPermissionState(result.state);
                if (result.state === 'granted') requestPosition();
                result.onchange = () => setPermissionState(result.state);
            }).catch(() => {});
        }
        return () => { if (localWatchRef) navigator.geolocation.clearWatch(localWatchRef); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Request position (called lazily on first "Near Me" click) */
    const requestPosition = useCallback(() => {
        if (!navigator.geolocation) {
            setError('Geolocation not supported');
            return Promise.resolve(null);
        }
        setLoading(true);
        return new Promise(resolve => {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                    setPosition(loc);
                    setError(null);
                    setLoading(false);
                    setPermissionState('granted');
                    resolve(loc);
                },
                (err) => {
                    setError(err.message);
                    setLoading(false);
                    if (err.code === 1) setPermissionState('denied');
                    resolve(null);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
            );
        });
    }, []);

    /* Distance from user to a point */
    const distanceTo = useCallback((lat, lng) => {
        if (!position) return null;
        return haversine(position.lat, position.lng, lat, lng);
    }, [position]);

    /* Filter items by radius (items must have .lat and .lng) */
    const filterNearby = useCallback((items, radiusMiles = 50) => {
        if (!position) return items;
        return items
            .map(item => ({ ...item, _distance: haversine(position.lat, position.lng, item.lat, item.lng) }))
            .filter(item => item._distance <= radiusMiles)
            .sort((a, b) => a._distance - b._distance);
    }, [position]);

    /* Sort items by distance (without filtering) */
    const sortByDistance = useCallback((items) => {
        if (!position) return items;
        return [...items]
            .map(item => ({ ...item, _distance: haversine(position.lat, position.lng, item.lat, item.lng) }))
            .sort((a, b) => a._distance - b._distance);
    }, [position]);

    const value = {
        position,
        error,
        loading,
        permissionState,
        requestPosition,
        distanceTo,
        filterNearby,
        sortByDistance,
        haversine,
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
}
